import { useMemo, useState, type ReactElement } from 'react';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type QueryKey,
} from '@tanstack/react-query';
import {
    CredentialRotationDisabledReason,
    IpxCredentialFieldClassification,
    type DashboardWebsiteCredentialPolicyListItem,
    type DashboardWebsiteCredentialSlot,
    type IpxCredentialFieldDescriptor,
    type IpxCredentialSaveFields,
    type IpxCredentialSummaryListItem,
    type PortalCredentialRotationEligibility,
    type RevealIpxCredentialSecretFieldResponse,
} from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge } from '@/components/ui/status-badge';
import { ipxCredentialManagementApi } from '@/lib/pillarApi';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useAuthStore } from '@/store/auth';
import { IpxCredentialFieldDisplayList } from './IpxCredentialFieldDisplayList';
import {
    IpxCredentialForm,
    type IpxCredentialFormInitialValues,
} from './IpxCredentialForm';
import { IpxCredentialRecoveryNotice } from './IpxCredentialRecoveryNotice';
import { IpxCredentialRotationButton } from './IpxCredentialRotationButton';
import {
    formatIpxCredentialPolicyKey,
    getIpxCredentialPolicySystemDisplayName,
} from './OrganizationIpxCredentialPoliciesCard';
import { validateIpxCredentialAccountId } from './ipxCredentialAccountIdValidation';

export interface DashboardWebsiteCredentialsCardProps {
    readonly organizationUuid: string;
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
    return error instanceof Error ? error.message : fallbackMessage;
}

function getCredentialFieldDescriptors(
    credential: IpxCredentialSummaryListItem,
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[]
): readonly IpxCredentialFieldDescriptor[] {
    return credential.field_descriptors.length === 0
        ? fieldDescriptors
        : credential.field_descriptors;
}

function buildCredentialFormInitialValues(
    credential: IpxCredentialSummaryListItem,
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[]
): IpxCredentialFormInitialValues {
    if (!credential.capabilities.can_display) {
        return {
            account_id: credential.account_id,
            field_values: {},
        };
    }
    const editableFieldValues: Record<string, string> = {};
    for (const descriptor of getCredentialFieldDescriptors(
        credential,
        fieldDescriptors
    )) {
        if (
            descriptor.field_classification ===
            IpxCredentialFieldClassification.PUBLIC
        ) {
            editableFieldValues[descriptor.field_name] =
                credential.public_fields[descriptor.field_name] ?? '';
        }
    }
    return {
        account_id: credential.account_id,
        field_values: editableFieldValues,
    };
}

function buildNewCredentialFormInitialValues(
    defaultAccountId: string
): IpxCredentialFormInitialValues {
    return {
        account_id: defaultAccountId,
        field_values: {},
    };
}

const CREATE_CREDENTIAL_DISABLED_REASON =
    'You do not have permission to create this website credential.';
const EDIT_CREDENTIAL_DISABLED_REASON =
    'You do not have permission to edit this website credential.';
const DELETE_CREDENTIAL_DISABLED_REASON =
    'You do not have permission to delete this website credential.';

const ROTATION_DISABLED_REASON_COPY: Record<
    CredentialRotationDisabledReason,
    string
> = {
    [CredentialRotationDisabledReason.NO_EFFECTIVE_CREDENTIAL]:
        'Create a credential before rotating it.',
    [CredentialRotationDisabledReason.MISSING_USE_PERMISSION]:
        'You do not have permission to use this website credential.',
    [CredentialRotationDisabledReason.MISSING_ROTATE_PERMISSION]:
        'You do not have permission to rotate this website credential.',
    [CredentialRotationDisabledReason.MISSING_AUDIT_PERMISSION]:
        'You do not have permission to view rotation history.',
    [CredentialRotationDisabledReason.NOT_ADMIN_FOR_ORGANIZATION]:
        'Only an authorized administrator can view rotation history.',
    [CredentialRotationDisabledReason.NO_ROTATION_FLOW]:
        'No credential rotation flow is configured.',
    [CredentialRotationDisabledReason.NO_ROTATABLE_SECRET_FIELDS]:
        'No secret credential fields are configured for rotation.',
    [CredentialRotationDisabledReason.ACTIVE_ATTEMPT_EXISTS]:
        'A credential rotation is already in progress.',
    [CredentialRotationDisabledReason.INVALID_GENERATION_POLICY]:
        'Credential generation rules are incomplete.',
    [CredentialRotationDisabledReason.APP_RUNTIME_REQUIRED]:
        'Open this website in the Anchorbase App to rotate credentials.',
};

function formatDescriptorLabels(
    descriptors: readonly IpxCredentialFieldDescriptor[]
): string {
    return descriptors.map((descriptor) => descriptor.display.label).join(', ');
}

function DashboardCredentialRotationEligibility(props: {
    readonly eligibility: PortalCredentialRotationEligibility | null;
}): ReactElement | null {
    const eligibility = props.eligibility;
    if (eligibility === null) {
        return null;
    }
    const rotatableFieldLabels = formatDescriptorLabels(
        eligibility.rotatable_field_descriptors
    );
    const flowLabels = eligibility.flow_summaries
        .map((flow) => flow.display_name)
        .join(', ');
    const rotateStatus = eligibility.can_rotate
        ? 'Rotation available'
        : `Rotation unavailable: ${
              ROTATION_DISABLED_REASON_COPY[
                  eligibility.rotate_disabled_reason ??
                      CredentialRotationDisabledReason.NO_ROTATION_FLOW
              ]
          }`;
    return (
        <div className="rounded border p-3 space-y-2">
            <div className="text-sm font-medium">Credential Rotation</div>
            <div className="text-sm text-muted-foreground">
                {rotateStatus}
            </div>
            {rotatableFieldLabels.length === 0 ? null : (
                <div className="text-sm text-muted-foreground">
                    Rotatable fields: {rotatableFieldLabels}
                </div>
            )}
            {flowLabels.length === 0 ? null : (
                <div className="text-sm text-muted-foreground">
                    Flows: {flowLabels}
                </div>
            )}
            <IpxCredentialRecoveryNotice
                recoverySummary={eligibility.recovery_summary}
            />
        </div>
    );
}

function DashboardWebsiteCredentialPolicyItem(props: {
    readonly item: DashboardWebsiteCredentialSlot;
    readonly queryKey: QueryKey;
}): ReactElement {
    const queryClient = useQueryClient();
    const [editingCredentialUuid, setEditingCredentialUuid] = useState<
        string | null
    >(null);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [credentialToDelete, setCredentialToDelete] =
        useState<IpxCredentialSummaryListItem | null>(null);
    const policy: DashboardWebsiteCredentialPolicyListItem = props.item.policy;
    const updateCredentialMutation = useMutation({
        mutationFn: async (variables: {
            readonly credentialUuid: string;
            readonly payload: IpxCredentialSaveFields;
        }): Promise<IpxCredentialSummaryListItem> =>
            await ipxCredentialManagementApi.updatePolicyCredential({
                credential_uuid: variables.credentialUuid,
                account_id: variables.payload.account_id,
                edited_fields: variables.payload.edited_fields,
            }),
        onSuccess: async () => {
            setEditingCredentialUuid(null);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
    });
    const createCredentialMutation = useMutation({
        mutationFn: async (
            payload: IpxCredentialSaveFields
        ): Promise<IpxCredentialSummaryListItem> =>
            await ipxCredentialManagementApi.createDashboardWebsiteCredential({
                organization_uuid: policy.organization_uuid,
                organization_ipx_credential_policy_uuid:
                    policy.organization_ipx_credential_policy_uuid,
                account_id: payload.account_id,
                edited_fields: payload.edited_fields,
            }),
        onSuccess: async () => {
            setIsCreating(false);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
    });
    const deleteCredentialMutation = useMutation({
        mutationFn: async (
            credentialUuid: string
        ): Promise<IpxCredentialSummaryListItem> =>
            await ipxCredentialManagementApi.deletePolicyCredential(
                credentialUuid
            ),
        onSuccess: async () => {
            setCredentialToDelete(null);
            await queryClient.invalidateQueries({ queryKey: props.queryKey });
        },
    });

    const createCredentialDisabledReason: string | undefined =
        props.item.can_create ? undefined : CREATE_CREDENTIAL_DISABLED_REASON;

    const renderCredential = (
        credential: IpxCredentialSummaryListItem
    ): ReactElement => {
        const isEditingCredential: boolean =
            editingCredentialUuid === credential.credential_uuid;
        return (
            <div
                key={credential.credential_uuid}
                className="rounded border p-3 space-y-3"
            >
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="font-medium">
                            {credential.account_id}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {credential.capabilities.can_display
                                ? credential.masked_preview
                                : 'Credential material hidden'}
                        </div>
                    </div>
                    {!isEditingCredential ? (
                        <div className="flex items-center gap-2">
                            <IpxCredentialRotationButton
                                canRotate={credential.capabilities.can_rotate}
                                disabledReason={
                                    credential.capabilities
                                        .rotate_disabled_reason
                                }
                            />
                            <span
                                className="inline-flex"
                                title={
                                    credential.capabilities.can_edit
                                        ? undefined
                                        : EDIT_CREDENTIAL_DISABLED_REASON
                                }
                            >
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={!credential.capabilities.can_edit}
                                    title={
                                        credential.capabilities.can_edit
                                            ? undefined
                                            : EDIT_CREDENTIAL_DISABLED_REASON
                                    }
                                    onClick={() => {
                                        setEditingCredentialUuid(
                                            credential.credential_uuid
                                        );
                                    }}
                                >
                                    Edit
                                </Button>
                            </span>
                            <span
                                className="inline-flex"
                                title={
                                    credential.capabilities.can_delete
                                        ? undefined
                                        : DELETE_CREDENTIAL_DISABLED_REASON
                                }
                            >
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={
                                        !credential.capabilities.can_delete ||
                                        deleteCredentialMutation.isPending
                                    }
                                    title={
                                        credential.capabilities.can_delete
                                            ? undefined
                                            : DELETE_CREDENTIAL_DISABLED_REASON
                                    }
                                    onClick={() => {
                                        setCredentialToDelete(credential);
                                    }}
                                >
                                    Delete
                                </Button>
                            </span>
                        </div>
                    ) : null}
                </div>
                {isEditingCredential ? (
                    <IpxCredentialForm
                        fieldDescriptors={getCredentialFieldDescriptors(
                            credential,
                            props.item.field_descriptors
                        )}
                        initialValues={buildCredentialFormInitialValues(
                            credential,
                            props.item.field_descriptors
                        )}
                        isSaving={updateCredentialMutation.isPending}
                        onSave={(payload) => {
                            updateCredentialMutation.mutate({
                                credentialUuid: credential.credential_uuid,
                                payload,
                            });
                        }}
                        validateSave={(payload) =>
                            validateIpxCredentialAccountId({
                                accountId: payload.account_id,
                                defaultAccountId:
                                    props.item.default_account_id,
                                credentials: props.item.credentials,
                                excludedCredentialUuid:
                                    credential.credential_uuid,
                            })}
                        onCancel={() => {
                            setEditingCredentialUuid(null);
                        }}
                    />
                ) : (
                    <IpxCredentialFieldDisplayList
                        fieldDescriptors={getCredentialFieldDescriptors(
                            credential,
                            props.item.field_descriptors
                        )}
                        publicFields={
                            credential.capabilities.can_display
                                ? credential.public_fields
                                : {}
                        }
                        canRevealSecretFields={
                            credential.capabilities.can_reveal_secret_fields
                        }
                        revealSecretFieldsDisabledReason={
                            credential.capabilities
                                .reveal_secret_fields_disabled_reason
                        }
                        onRevealSecretField={(
                            fieldName: string
                        ): Promise<RevealIpxCredentialSecretFieldResponse> =>
                            ipxCredentialManagementApi.revealPolicyCredentialSecretField(
                                {
                                    credential_uuid: credential.credential_uuid,
                                    field_name: fieldName,
                                }
                            )}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="rounded border p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium">
                        {policy.ipx_credential_definition_name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {getIpxCredentialPolicySystemDisplayName(policy)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Key: {formatIpxCredentialPolicyKey(policy)}
                    </div>
                </div>
                <StatusBadge status={policy.status} />
            </div>
            <DashboardCredentialRotationEligibility
                eligibility={props.item.rotation_eligibility}
            />

            {updateCredentialMutation.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            updateCredentialMutation.error,
                            'Failed to update website credential'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}
            {createCredentialMutation.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            createCredentialMutation.error,
                            'Failed to create website credential'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}
            {deleteCredentialMutation.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            deleteCredentialMutation.error,
                            'Failed to delete website credential'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}

            <div className="space-y-3">
                {props.item.credentials.length === 0 ? (
                    <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                            No credential defined.
                        </div>
                        <div className="space-y-1">
                            {props.item.field_descriptors.map((descriptor) => (
                                <div
                                    key={descriptor.field_name}
                                    className="text-sm"
                                >
                                    {descriptor.display.label}
                                    {descriptor.required ? (
                                        <span className="text-muted-foreground">
                                            {' '}
                                            Required
                                        </span>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    props.item.credentials.map(renderCredential)
                )}
                {isCreating ? (
                    <IpxCredentialForm
                        fieldDescriptors={props.item.field_descriptors}
                        initialValues={buildNewCredentialFormInitialValues(
                            props.item.default_account_id
                        )}
                        isSaving={createCredentialMutation.isPending}
                        saveLabel="Create"
                        requireCompleteFields
                        onSave={(payload) => {
                            createCredentialMutation.mutate(payload);
                        }}
                        validateSave={(payload) =>
                            validateIpxCredentialAccountId({
                                accountId: payload.account_id,
                                defaultAccountId: props.item.default_account_id,
                                credentials: props.item.credentials,
                            })}
                        onCancel={() => {
                            setIsCreating(false);
                        }}
                    />
                ) : (
                    <span
                        className="inline-flex"
                        title={createCredentialDisabledReason}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={
                                createCredentialDisabledReason !== undefined
                            }
                            title={createCredentialDisabledReason}
                            onClick={() => {
                                setIsCreating(true);
                            }}
                        >
                            Create
                        </Button>
                    </span>
                )}
            </div>
            <Dialog
                open={credentialToDelete !== null}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setCredentialToDelete(null);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Website Credential</DialogTitle>
                        <DialogDescription>
                            {credentialToDelete === null
                                ? 'Delete this website credential?'
                                : `Delete website credential for ${credentialToDelete.account_id}?`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCredentialToDelete(null);
                            }}
                            disabled={deleteCredentialMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (credentialToDelete === null) {
                                    return;
                                }
                                deleteCredentialMutation.mutate(
                                    credentialToDelete.credential_uuid
                                );
                            }}
                            disabled={
                                credentialToDelete === null ||
                                deleteCredentialMutation.isPending
                            }
                        >
                            {deleteCredentialMutation.isPending
                                ? 'Deleting...'
                                : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export function DashboardWebsiteCredentialsCard(
    props: DashboardWebsiteCredentialsCardProps
): ReactElement {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const credentialsQueryKey = useMemo(
        () =>
            buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'dashboard',
                'website-credentials',
                props.organizationUuid,
            ]),
        [activeRoleAssignmentId, props.organizationUuid]
    );
    const credentialsQuery = useQuery({
        queryKey: credentialsQueryKey,
        queryFn: async () =>
            await ipxCredentialManagementApi.listDashboardWebsiteCredentials({
                organization_uuid: props.organizationUuid,
            }),
    });
    const credentialSlots: DashboardWebsiteCredentialSlot[] =
        credentialsQuery.data?.credential_slots ?? [];

    return (
        <Card className="max-w-4xl mt-6">
            <CardHeader>
                <CardTitle>Website Credentials</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {credentialsQuery.error ? (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {getErrorMessage(
                                credentialsQuery.error,
                                'Failed to load website credentials'
                            )}
                        </AlertDescription>
                    </Alert>
                ) : null}
                {credentialsQuery.isLoading ? (
                    <p className="text-muted-foreground">
                        Loading website credentials...
                    </p>
                ) : null}
                {!credentialsQuery.isLoading && credentialSlots.length === 0 ? (
                    <p className="text-muted-foreground">
                        No website credentials are available.
                    </p>
                ) : null}
                <div className="space-y-3">
                    {credentialSlots.map((item) => (
                        <DashboardWebsiteCredentialPolicyItem
                            key={
                                item.policy
                                    .organization_ipx_credential_policy_uuid
                            }
                            item={item}
                            queryKey={credentialsQueryKey}
                        />
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}
